import { resolveBinding, evaluate, lambdaSourceOf, createMinimalContext } from '@almadar/evaluator';
export { createMinimalContext } from '@almadar/evaluator';
import { isKnownStdOperator } from '@almadar/std/registry';
import { containsEntityBinding, containsPayloadBinding, RENDER_BINDING_MARKER } from '@almadar/core';
import { createLogger, setNamespaceLevel } from '@almadar/logger';

// src/entities/entityAccess.ts
var bindLog = createLogger("almadar:runtime:bindings");
setNamespaceLevel("almadar:runtime:bindings", "WARN");
var deferLog = createLogger("almadar:runtime:defer");
var renderLog = createLogger("almadar:runtime:render-ui");
var CLIENT_ONLY_BINDING_ROOTS = /* @__PURE__ */ new Set(["trait"]);
function isClientOnlyBinding(value) {
  if (!value.startsWith("@")) return false;
  const afterAt = value.slice(1);
  const firstDot = afterAt.indexOf(".");
  const root = firstDot === -1 ? afterAt : afterAt.slice(0, firstDot);
  return CLIENT_ONLY_BINDING_ROOTS.has(root);
}
function interpolateProps(props, ctx) {
  const result = {};
  let anyChanged = false;
  for (const [key, value] of Object.entries(props)) {
    const interpolated = interpolateValue(value, ctx);
    result[key] = interpolated;
    if (interpolated !== value) anyChanged = true;
  }
  const entityBindingRaw = props["entity"];
  const typeBindingRaw = props["type"];
  const patternType = typeof typeBindingRaw === "string" ? typeBindingRaw : void 0;
  if (typeof entityBindingRaw === "string") {
    renderLog.debug("interpolateProps:entity", () => {
      const resolvedEntity = result["entity"];
      const resolvedRow = resolvedEntity !== null && typeof resolvedEntity === "object" && !Array.isArray(resolvedEntity) ? resolvedEntity : null;
      const ctxRow = ctx.payload["row"];
      const ctxPayloadKeys = Object.keys(ctx.payload).join(",");
      const payloadDataRaw = ctx.payload["data"];
      const payloadDataLen = Array.isArray(payloadDataRaw) ? payloadDataRaw.length : null;
      const ctxEntityRaw = ctx.entity;
      const ctxEntityLen = Array.isArray(ctxEntityRaw) ? ctxEntityRaw.length : null;
      const resolvedLen = Array.isArray(resolvedEntity) ? resolvedEntity.length : null;
      return {
        patternType,
        entityBinding: entityBindingRaw,
        resolvedIsObject: resolvedRow !== null,
        resolvedIsArray: Array.isArray(resolvedEntity),
        resolvedLen,
        resolvedEqualsCtxRow: ctxRow !== void 0 && resolvedRow !== null && resolvedRow === ctxRow,
        resolvedRowId: resolvedRow?.id,
        ctxPayloadKeys,
        ctxPayloadDataLen: payloadDataLen,
        ctxEntityIsArray: Array.isArray(ctxEntityRaw),
        ctxEntityLen
      };
    });
  }
  if (patternType === "form-section" || patternType === "form") {
    bindLog.debug("form-binding", () => {
      const modeRaw = result["mode"];
      const submitRaw = result["submitEvent"];
      const cancelRaw = result["cancelEvent"];
      return {
        patternType,
        mode: typeof modeRaw === "string" ? modeRaw : void 0,
        submitEvent: typeof submitRaw === "string" ? submitRaw : void 0,
        cancelEvent: typeof cancelRaw === "string" ? cancelRaw : void 0,
        entity: JSON.stringify(result["entity"] ?? null),
        fields: JSON.stringify(result["fields"] ?? null)
      };
    });
  }
  return anyChanged ? result : props;
}
function interpolateValue(value, ctx) {
  if (value === null || value === void 0) {
    return value;
  }
  if (typeof value === "string") {
    return interpolateString(value, ctx);
  }
  if (Array.isArray(value)) {
    return interpolateArray(value, ctx);
  }
  if (typeof value === "object") {
    return interpolateProps(value, ctx);
  }
  return value;
}
function resolveConfigLeavesInLambdaBody(node, ctx) {
  if (typeof node === "string") {
    if (!node.startsWith("@config.") || node.includes(" ")) return node;
    const resolved = resolveBinding(node, ctx);
    deferLog.debug("lambda:config-leaf", () => ({
      binding: node,
      resolvedType: typeof resolved,
      selfForward: resolved === node
    }));
    return resolved === node ? void 0 : resolved;
  }
  if (Array.isArray(node)) {
    let changed = false;
    const out = node.map((item) => {
      const next = resolveConfigLeavesInLambdaBody(item, ctx);
      if (next !== item) changed = true;
      return next;
    });
    return changed ? out : node;
  }
  if (node !== null && typeof node === "object" && !(node instanceof Date)) {
    let changed = false;
    const out = {};
    for (const [key, item] of Object.entries(node)) {
      const next = resolveConfigLeavesInLambdaBody(item, ctx);
      if (next !== item) changed = true;
      out[key] = next;
    }
    return changed ? out : node;
  }
  return node;
}
function deferEntityBindings(value, ctx, configHops = 0) {
  if (typeof value === "string") {
    if (containsEntityBinding(value) && !containsPayloadBinding(value)) {
      return { [RENDER_BINDING_MARKER]: true, expression: value };
    }
    if (value.startsWith("@config.") && !value.includes(" ") && configHops < 8) {
      const hop = ctx.config?.[value.slice("@config.".length)];
      deferLog.debug("defer:config-hop", () => ({
        forward: value,
        hopType: typeof hop,
        hopPreview: typeof hop === "string" ? hop : Array.isArray(hop) ? "array" : hop === void 0 ? "undefined" : "object"
      }));
      if (hop !== void 0 && hop !== value) {
        return deferEntityBindings(hop, ctx, configHops + 1);
      }
    }
    return interpolateValue(value, ctx);
  }
  if (Array.isArray(value)) {
    if (value.length === 3 && value[0] === "fn" && typeof value[1] === "string") {
      return resolveConfigLeavesInLambdaBody(value, ctx);
    }
    if (isSExpression(value)) {
      if (containsEntityBinding(value) && !containsPayloadBinding(value)) {
        return { [RENDER_BINDING_MARKER]: true, expression: value };
      }
      return interpolateValue(value, ctx);
    }
    return value.map((item) => deferEntityBindings(item, ctx));
  }
  if (value !== null && typeof value === "object") {
    const out = {};
    for (const [key, item] of Object.entries(value)) {
      out[key] = deferEntityBindings(item, ctx);
    }
    return out;
  }
  return value;
}
var inFlightConfigRecursions = /* @__PURE__ */ new Set();
function interpolateString(value, ctx) {
  if (value.startsWith("@") && isPureBinding(value)) {
    if (isClientOnlyBinding(value)) {
      bindLog.debug("passthrough:client-only", { binding: value });
      return value;
    }
    const resolved = resolveBinding(value, ctx);
    bindLog.debug("resolve", { binding: value, resolvedType: typeof resolved });
    if (value.startsWith("@config.") && resolved !== null && typeof resolved === "object" && containsBindings(resolved) && !inFlightConfigRecursions.has(value)) {
      inFlightConfigRecursions.add(value);
      try {
        bindLog.debug("resolve:config-recurse", { binding: value });
        return interpolateValue(resolved, ctx);
      } finally {
        inFlightConfigRecursions.delete(value);
      }
    }
    return resolved;
  }
  if (value.includes("@")) {
    return interpolateEmbeddedBindings(value, ctx);
  }
  return value;
}
function isPureBinding(value) {
  return /^@[\w]+(?:\[\d+\])*(?:\.[\w]+(?:\[\d+\])*)*$/.test(value);
}
function interpolateEmbeddedBindings(value, ctx) {
  return value.replace(/@[\w]+(?:\[\d+\])*(?:\.[\w]+(?:\[\d+\])*)*/g, (match) => {
    if (isClientOnlyBinding(match)) {
      return match;
    }
    const resolved = resolveBinding(match, ctx);
    return resolved !== void 0 ? String(resolved) : match;
  });
}
function interpolateArray(value, ctx) {
  if (value.length === 0) {
    return value;
  }
  if (Array.isArray(value) && value.length === 3 && value[0] === "fn" && typeof value[1] === "string") {
    return resolveConfigLeavesInLambdaBody(value, ctx);
  }
  if (isSExpression(value)) {
    const evaluated = evaluate(value, ctx);
    const result = storesLambdaInLiteral(value) ? restoreLambdaSources(evaluated) : evaluated;
    bindLog.debug("sexpr:eval", () => ({
      operator: typeof value[0] === "string" ? value[0] : "<non-string>",
      argCount: value.length - 1,
      inputJson: JSON.stringify(value).slice(0, 300),
      resultType: typeof result,
      resultJson: typeof result === "object" && result !== null ? JSON.stringify(result).slice(0, 2e3) : String(result)
    }));
    return result;
  }
  const mapped = [];
  let anyChanged = false;
  for (let i = 0; i < value.length; i++) {
    const item = value[i];
    if (Array.isArray(item) && isRenderChildrenMap(item)) {
      const expanded = evaluate(item, ctx);
      if (Array.isArray(expanded)) {
        for (const node of expanded) mapped.push(node);
      }
      anyChanged = true;
      continue;
    }
    const interpolated = interpolateValue(item, ctx);
    mapped.push(interpolated);
    if (interpolated !== item) anyChanged = true;
  }
  return anyChanged ? mapped : value;
}
function isFnForm(node) {
  return Array.isArray(node) && (node[0] === "fn" || node[0] === "lambda");
}
function isPlainRecord(value) {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}
function containsFnForm(node) {
  if (isFnForm(node)) return true;
  if (Array.isArray(node)) return node.some(containsFnForm);
  if (isPlainRecord(node)) return Object.values(node).some(containsFnForm);
  return false;
}
var storesLambdaCache = /* @__PURE__ */ new WeakMap();
function storesLambdaInLiteral(node) {
  if (node === null || typeof node !== "object") return false;
  const cached = storesLambdaCache.get(node);
  if (cached !== void 0) return cached;
  let found;
  if (isPlainRecord(node)) {
    found = Object.values(node).some(containsFnForm) || Object.values(node).some(storesLambdaInLiteral);
  } else if (Array.isArray(node)) {
    found = node.some(storesLambdaInLiteral);
  } else {
    found = false;
  }
  storesLambdaCache.set(node, found);
  return found;
}
function restoreLambdaSources(value) {
  if (typeof value === "function") return lambdaSourceOf(value) ?? value;
  if (Array.isArray(value)) {
    let changed = false;
    const out = value.map((item) => {
      const next = restoreLambdaSources(item);
      if (next !== item) changed = true;
      return next;
    });
    return changed ? out : value;
  }
  if (isPlainRecord(value)) {
    let changed = false;
    const out = {};
    for (const [key, item] of Object.entries(value)) {
      const next = restoreLambdaSources(item);
      if (next !== item) changed = true;
      out[key] = next;
    }
    return changed ? out : value;
  }
  return value;
}
function isRenderChildrenMap(value) {
  if (value.length !== 3 || value[0] !== "array/map") return false;
  const lambda = value[2];
  return Array.isArray(lambda) && lambda.length === 3 && lambda[0] === "fn" && typeof lambda[1] === "string";
}
function isSExpression(value) {
  if (value.length === 0) return false;
  const first = value[0];
  if (typeof first !== "string") return false;
  if (isKnownStdOperator(first)) return true;
  if (first.includes("/")) return true;
  if (first === "lambda" || first === "let") return true;
  return false;
}
function containsBindings(value) {
  if (typeof value === "string") {
    return value.includes("@");
  }
  if (Array.isArray(value)) {
    return value.some(containsBindings);
  }
  if (value !== null && typeof value === "object") {
    return Object.values(value).some(containsBindings);
  }
  return false;
}
function extractBindings(value) {
  const bindings = [];
  function collect(v) {
    if (typeof v === "string") {
      const matches = v.match(/@[\w]+(?:\.[\w]+)*/g);
      if (matches) {
        bindings.push(...matches);
      }
    } else if (Array.isArray(v)) {
      v.forEach(collect);
    } else if (v !== null && typeof v === "object") {
      Object.values(v).forEach(collect);
    }
  }
  collect(value);
  return [...new Set(bindings)];
}
function createContextFromBindings(bindings, strictBindings, contextExtensions) {
  const ctx = createMinimalContext(
    bindings.entity || {},
    bindings.payload || {},
    bindings.state || "idle"
  );
  if (strictBindings) {
    ctx.strictBindings = true;
  }
  if (bindings.config) {
    ctx.config = bindings.config;
  }
  if (bindings.user) {
    ctx.user = bindings.user;
  }
  if (bindings.now !== void 0) {
    ctx.now = bindings.now;
  }
  if (bindings.locale !== void 0) ctx.locale = bindings.locale;
  if (bindings.messages !== void 0) ctx.messages = bindings.messages;
  if (bindings.event !== void 0) ctx.event = bindings.event;
  if (bindings.prevEvents !== void 0) ctx.prevEvents = bindings.prevEvents;
  if (bindings.prevStates !== void 0) ctx.prevStates = bindings.prevStates;
  if (bindings.fromState !== void 0) ctx.fromState = bindings.fromState;
  if (bindings.toState !== void 0) ctx.toState = bindings.toState;
  if (bindings.pages) {
    ctx.pages = bindings.pages;
  }
  if (bindings.currentTheme) {
    ctx.currentTheme = bindings.currentTheme;
  }
  if (bindings.callsitePayload) {
    ctx.callsitePayload = bindings.callsitePayload;
  }
  if (bindings.locals) {
    ctx.locals = bindings.locals;
  }
  if (contextExtensions) {
    Object.assign(ctx, contextExtensions);
  }
  return ctx;
}

// src/entities/entityAccess.ts
function accessDeniedMessage(op, entityType) {
  return `@${op} denied: the declared access policy for '${entityType}' rejected this row`;
}
function applyRowAccess(rows, policy, filter, bindings) {
  if (policy === void 0 && filter === void 0) {
    return rows;
  }
  const predicates = [policy, filter].filter((p) => p !== void 0);
  return rows.filter((entity) => {
    const ctx = createContextFromBindings(
      { entity, payload: bindings.payload, user: bindings.user, config: bindings.config, ...bindings.now !== void 0 ? { now: bindings.now } : {} },
      false
    );
    return predicates.every((predicate) => {
      try {
        return Boolean(evaluate(predicate, ctx));
      } catch (err) {
        bindings.onPredicateError?.(err instanceof Error ? err : new Error(String(err)));
        return false;
      }
    });
  });
}
function checkMutationAccess(row, policy, bindings) {
  if (policy === void 0) {
    return true;
  }
  const ctx = createContextFromBindings(
    { entity: row, payload: bindings.payload, user: bindings.user, config: bindings.config, ...bindings.now !== void 0 ? { now: bindings.now } : {} },
    false
  );
  try {
    return Boolean(evaluate(policy, ctx));
  } catch (err) {
    bindings.onPredicateError?.(err instanceof Error ? err : new Error(String(err)));
    return false;
  }
}

export { accessDeniedMessage, applyRowAccess, checkMutationAccess, containsBindings, createContextFromBindings, deferEntityBindings, extractBindings, interpolateProps, interpolateValue, isSExpression };
